import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FIREBASE_CONFIG } from '../../../config/Config';
import { subscribeInvoicesByClient } from '../../../services/FinancialHealthWidgetRepository';

const toDateObj = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isFinite(raw.getTime()) ? raw : null;
  if (typeof raw?.toDate === 'function') {
    try {
      const d = raw.toDate();
      return d instanceof Date && Number.isFinite(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  const s = String(raw || '').trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
};

const formatCurrencyFromCents = (cents) => {
  const n = Number(cents);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(safe / 100);
};

export default function FinancialHealthWidget({ clientId }) {
  const cid = useMemo(() => String(clientId || '').trim(), [clientId]);
  const appId = useMemo(() => String(FIREBASE_CONFIG?.projectId || '').trim(), []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  const lastClientIdRef = useRef(cid);

  useEffect(() => {
    lastClientIdRef.current = cid;
  }, [cid]);

  useEffect(() => {
    const id = String(cid || '').trim();
    if (!id) {
      setRows([]);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const unsub = subscribeInvoicesByClient(id, (out) => {
      out.sort((a, b) => {
        const ad = toDateObj(a?.dueDate);
        const bd = toDateObj(b?.dueDate);
        const av = ad ? ad.getTime() : 0;
        const bv = bd ? bd.getTime() : 0;
        return av - bv;
      });
      setRows(out);
      setLoading(false);
    }, (e) => {
      setError(String(e?.message || e || 'Falha ao carregar faturas.'));
      setLoading(false);
    });

    return () => {
      try { unsub && unsub(); } catch { void 0; }
    };
  }, [cid, appId]);

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const activeRows = useMemo(() => {
    return (Array.isArray(rows) ? rows : []).filter((r) => {
      const status = String(r?.status || '').trim().toUpperCase();
      return status !== 'PAID';
    });
  }, [rows]);

  const totals = useMemo(() => {
    let operating = 0;
    let trust = 0;

    (Array.isArray(rows) ? rows : []).forEach((r) => {
      const status = String(r?.status || '').trim().toUpperCase();
      if (status !== 'PENDING') return;

      const t = String(r?.type || '').trim().toUpperCase();
      const amt = Number(r?.amountDue) || 0;
      if (t === 'TRUST') trust += amt;
      else if (t === 'OPERATING') operating += amt;
    });

    return { operating, trust };
  }, [rows]);

  return (
    <div className="p-4 rounded-2xl border border-slate-200 bg-white">
      <div className="text-sm font-extrabold text-[#20243B]">Saúde Financeira</div>
      <div className="text-[11px] text-slate-500 mt-1">Separação visual obrigatória: Escritório (OPERATING) vs Cliente (TRUST).</div>

      {error ? <div className="mt-3 p-3 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700">{error}</div> : null}
      {loading ? <div className="mt-3 text-xs text-slate-500">Carregando faturas...</div> : null}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-3 rounded-xl border border-slate-200 bg-slate-50">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-600">Receita Pendente (Honorários)</div>
          <div className="mt-1 text-lg font-extrabold text-slate-900">{formatCurrencyFromCents(totals.operating)}</div>
        </div>
        <div className="p-3 rounded-xl border border-slate-200 bg-slate-50">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-600">Provisão de Custas (Trust)</div>
          <div className="mt-1 text-lg font-extrabold text-slate-900">{formatCurrencyFromCents(totals.trust)}</div>
        </div>
      </div>

      <div className="mt-5">
        <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-600">Faturas Ativas</div>

        {!loading && activeRows.length === 0 ? (
          <div className="mt-2 text-xs text-slate-500">Nenhuma fatura ativa.</div>
        ) : null}

        {activeRows.length > 0 ? (
          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left py-2 pr-3">Vencimento</th>
                  <th className="text-left py-2 pr-3">Tipo</th>
                  <th className="text-left py-2 pr-3">Método</th>
                  <th className="text-right py-2">Valor</th>
                </tr>
              </thead>
              <tbody>
                {activeRows.map((r, idx) => {
                  const status = String(r?.status || '').trim().toUpperCase();
                  const due = toDateObj(r?.dueDate);
                  const isOverdue = status === 'PENDING' && due && due.getTime() < todayStart.getTime();
                  const type = String(r?.type || '').trim().toUpperCase();
                  const method = String(r?.paymentIntent?.method || '').trim().toUpperCase();
                  const amount = Number(r?.amountDue) || 0;

                  return (
                    <tr
                      key={String(r?.id || r?.invoiceId || idx)}
                      className={
                        'border-t ' +
                        (isOverdue ? 'bg-red-50 border-red-200 text-red-800' : 'border-slate-100 text-slate-700')
                      }
                    >
                      <td className="py-2 pr-3 whitespace-nowrap">
                        {due ? due.toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{type || '—'}</td>
                      <td className="py-2 pr-3 whitespace-nowrap">{method || '—'}</td>
                      <td className="py-2 text-right whitespace-nowrap">{formatCurrencyFromCents(amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
